import { ReactNode } from "react";
import {
  Button as ChakraButton,
  ButtonProps as ChakraButtonProps,
  SystemStyleObject,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";

export interface ButtonProps extends Omit<ChakraButtonProps, "css" | "color"> {
  children: ReactNode;
  variant?: "primary" | "secondary" | "outline" | "ghost";
  isLoading?: boolean;
  isDisabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  to?: string;
  type?: "button" | "submit" | "reset";
}

const KYDButton = ({
  children,
  variant = "primary",
  isLoading = false,
  isDisabled = false,
  onClick,
  backgroundColor,
  bg,
  to,
  type = "button",
  ...props
}: ButtonProps) => {
  const getButtonStyles = (): SystemStyleObject => {
    const baseStyles = (() => {
      switch (variant) {
        case "primary":
          return {
            color: "white",
            bg: "green.500",
            _hover: { opacity: 0.8 },
          };
        case "secondary":
          return {
            color: "#1A202C",
            bg: "gray.100",
            _hover: { opacity: 0.8 },
          };
        case "outline":
          return {
            color: "#1A202C",
            fontWeight: "400",
            bg: "white",
            border: "1px solid #00000033",
            _hover: { opacity: 0.8 },
          };
        case "ghost":
          return {
            color: "#888888",
            fontWeight: "400",
            bg: "transparent",
            border: "none",
            boxShadow: "none",
            _hover: { opacity: 0.8 },
          };
        default:
          return {
            color: "#1A202C",
            fontWeight: "400",
            bg: "white",
            border: "1px solid #00000033",
            _hover: { opacity: 0.8 },
          };
      }
    })();

    // Override bg if provided
    if (backgroundColor || bg) {
      return {
        ...baseStyles,
        bg: backgroundColor || bg,
      };
    }

    return baseStyles;
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (onClick) {
      event.preventDefault();
      onClick(event);
    }
  };

  const buttonProps = {
    isLoading,
    isDisabled,
    onClick: handleClick,
    paddingX: 6,
    sx: {
      ...getButtonStyles(),
      opacity: isDisabled ? 0.3 : 1,
    },
    backgroundColor,
    bg,
    ...props,
  };

  if (to) {
    return (
      <RouterLink to={to} style={{ textDecoration: "none" }}>
        <ChakraButton {...buttonProps}>{children}</ChakraButton>
      </RouterLink>
    );
  }

  return <ChakraButton {...buttonProps}>{children}</ChakraButton>;
};

export default KYDButton;
